import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Alert,
  Stack,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Tabs,
  Tab,
  Badge,
} from "@mui/material";
import moment from "moment";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";
import { useWorkoutImportJobs } from "../../contexts/WorkoutImportJobsContext";
import AddWorkoutDetails from "../AddWorkoutDetails";
import {
  momentFromWorkoutTimestamp,
  workoutDateToUnixSeconds,
} from "../../utils/workoutDate";
import {
  buildTemplateWeeksFromImport,
  defaultImportedProgramName,
  mergeProgramWeeks,
  workoutToTemplateInfo,
} from "../../utils/workoutImportProgram";

const WorkoutImport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobIdFromUrl = searchParams.get("jobId")?.trim() || null;
  const destinationFromUrl = searchParams.get("destination");
  const programIdFromUrl = searchParams.get("programId")?.trim() || null;

  const {
    getClients,
    addWorkoutForUser,
    getPrograms,
    createProgram,
    updateProgram,
    createWorkoutTemplate,
  } = useApi();
  const { activeJobs, fetchJob, createImportJob, rememberImportJob } =
    useWorkoutImportJobs();
  const {
    isCoach,
    loading: userInfoLoading,
    id: coachId,
    name: coachName,
    email: coachEmail,
  } = useUserInfo();
  const [clients, setClients] = useState([]);
  const [coachProfile, setCoachProfile] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [rawText, setRawText] = useState("");
  const [defaultWeightUnit, setDefaultWeightUnit] = useState("lbs");
  const [weeks, setWeeks] = useState([]);
  const [programLength, setProgramLength] = useState(1);
  const [selectedWorkoutKey, setSelectedWorkoutKey] = useState(null); // Format: "week-{weekNum}-{workoutIdx}"
  const [statusMessage, setStatusMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [savedDates, setSavedDates] = useState({});
  const [saveDestination, setSaveDestination] = useState("client");
  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [programTargetId, setProgramTargetId] = useState("new");
  const [newProgramName, setNewProgramName] = useState("");
  const [programMergeMode, setProgramMergeMode] = useState("replace");
  const [savingToProgram, setSavingToProgram] = useState(false);
  const [savedProgramId, setSavedProgramId] = useState(null);
  const [urlJob, setUrlJob] = useState(null);
  const [urlJobLoadError, setUrlJobLoadError] = useState(false);
  const resultsPaperRef = useRef(null);
  const urlJobPollTicks = useRef(0);

  const awaitingUrlJobHydrate =
    !!jobIdFromUrl && !urlJob && !urlJobLoadError && !userInfoLoading;

  const importBlocked =
    activeJobs.length > 0 ||
    (urlJob &&
      (urlJob.status === "pending" || urlJob.status === "running")) ||
    awaitingUrlJobHydrate;

  const isProgramDestination = saveDestination === "program";

  const workoutTabs = useMemo(() => {
    const tabs = [];
    for (const weekData of weeks) {
      (weekData.workouts || []).forEach((item, idx) => {
        tabs.push({
          key: `week-${weekData.week}-${idx}`,
          week: weekData.week,
          workoutIndex: idx,
          item,
          label: item.workout?.name?.trim() || `Workout ${idx + 1}`,
        });
      });
    }
    return tabs;
  }, [weeks]);

  const showWorkoutTabs = workoutTabs.length > 1;
  const showWeekPrefixOnTabs = weeks.length > 1;

  useEffect(() => {
    if (!workoutTabs.length) {
      if (selectedWorkoutKey) setSelectedWorkoutKey(null);
      return;
    }
    if (!selectedWorkoutKey || !workoutTabs.some((t) => t.key === selectedWorkoutKey)) {
      setSelectedWorkoutKey(workoutTabs[0].key);
    }
  }, [workoutTabs, selectedWorkoutKey]);

  useEffect(() => {
    if (destinationFromUrl === "program" || programIdFromUrl) {
      setSaveDestination("program");
    }
    if (programIdFromUrl) {
      setProgramTargetId(programIdFromUrl);
    }
  }, [destinationFromUrl, programIdFromUrl]);

  useEffect(() => {
    const loadPrograms = async () => {
      if (!isCoach || userInfoLoading) return;
      setLoadingPrograms(true);
      try {
        const res = await getPrograms();
        const sorted =
          res?.sort((a, b) => (a.name || "").localeCompare(b.name || "")) || [];
        setPrograms(sorted);
        if (
          programIdFromUrl &&
          sorted.some((p) => p.id === programIdFromUrl)
        ) {
          setProgramTargetId(programIdFromUrl);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingPrograms(false);
      }
    };
    loadPrograms();
  }, [getPrograms, isCoach, userInfoLoading, programIdFromUrl]);

  useEffect(() => {
    if (!isProgramDestination || newProgramName.trim() || !weeks.length) {
      return;
    }
    setNewProgramName(defaultImportedProgramName(weeks));
  }, [weeks, isProgramDestination, newProgramName]);

  useEffect(() => {
    const loadClients = async () => {
      setLoadingClients(true);
      try {
        const res = await getClients();
        const active = res.filter((c) => !c.deleted);
        setClients(active);

        // Create coach profile object
        if (coachId && coachName) {
          const coach = {
            id: coachId,
            name: coachName,
            email: coachEmail || "",
            isCoach: true,
          };
          setCoachProfile(coach);
          // Prepend coach to clients list and set as default selection
          const allClients = [coach, ...active];
          setSelectedClientId(coachId);
        } else if (active.length) {
          setSelectedClientId(active[0].id);
        }
      } catch (err) {
        console.error(err);
        setError("Unable to load clients.");
      } finally {
        setLoadingClients(false);
      }
    };
    loadClients();
  }, [getClients, coachId, coachName, coachEmail]);

  const handleFileUpload = (evt) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setRawText(e.target.result);
    };
    reader.readAsText(file);
  };

  const applyImportResult = useCallback((resp) => {
    const weeksData = resp?.weeks || [];
    const programLen = resp?.programLength || 1;

    if (!weeksData.length) {
      setError("No workouts found in the provided text.");
      return;
    }

    setError("");

    const processedWeeks = weeksData.map((weekData) => ({
      week: weekData.week,
      workouts: (weekData.workouts || []).map((w) => ({
        clientTempId: w.clientTempId,
        order: w.order,
        warnings: w.warnings || [],
        workout: {
          ...(w.workout || {}),
          exercises: w.workout?.exercises || [],
        },
      })),
    }));

    setWeeks(processedWeeks);
    setProgramLength(programLen);

    if (processedWeeks.length > 0 && processedWeeks[0].workouts.length > 0) {
      setSelectedWorkoutKey(`week-${processedWeeks[0].week}-0`);
    }
  }, []);

  useEffect(() => {
    if (!jobIdFromUrl) {
      setUrlJob(null);
      setUrlJobLoadError(false);
      urlJobPollTicks.current = 0;
      return undefined;
    }
    if (!isCoach || userInfoLoading) {
      return undefined;
    }

    let cancelled = false;
    let intervalId;
    const clearPoll = () => {
      if (intervalId != null) {
        clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    urlJobPollTicks.current = 0;
    setUrlJobLoadError(false);
    setError("");

    const tick = async () => {
      if (cancelled) return;
      urlJobPollTicks.current += 1;
      if (urlJobPollTicks.current > 120) {
        setError("Import timed out. Try again in a moment.");
        clearPoll();
        return;
      }
      try {
        const job = await fetchJob(jobIdFromUrl);
        if (cancelled) return;
        if (!job?.jobId) {
          setError("Job not found.");
          setUrlJobLoadError(true);
          setUrlJob(null);
          clearPoll();
          return;
        }
        setUrlJob(job);

        if (job.status === "completed") {
          clearPoll();
          rememberImportJob(jobIdFromUrl, { status: "completed" });
          if (job.result) {
            applyImportResult(job.result);
            if (job.targetUserId) {
              setSelectedClientId(String(job.targetUserId));
            }
            setTimeout(() => {
              resultsPaperRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }, 400);
          } else {
            setError("Import finished but no result was returned.");
          }
          return;
        }

        if (job.status === "failed") {
          clearPoll();
          rememberImportJob(jobIdFromUrl, { status: "failed" });
          setError(job.errorMessage || "Import failed.");
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setUrlJobLoadError(true);
          setUrlJob(null);
          setError("Could not load import job.");
          clearPoll();
        }
      }
    };

    tick();
    intervalId = setInterval(tick, 2500);

    return () => {
      cancelled = true;
      clearPoll();
    };
  }, [
    jobIdFromUrl,
    isCoach,
    userInfoLoading,
    fetchJob,
    applyImportResult,
    rememberImportJob,
  ]);

  const handleImport = async () => {
    setError("");
    setStatusMessage("");
    setWeeks([]);
    setSelectedWorkoutKey(null);
    if (!rawText.trim()) {
      setError("Paste or upload workout text before importing.");
      return;
    }
    if (importBlocked) {
      setError(
        "An import is already in progress. Wait for it to finish before starting another.",
      );
      return;
    }

    try {
      setImporting(true);
      const started = await createImportJob({
        rawText,
        defaultWeightUnit,
        targetUserId: selectedClientId || undefined,
      });
      const newJobId = started?.jobId;
      if (!newJobId) {
        setError(started?.message || "Could not start import job.");
        return;
      }
      navigate(`/workout-import?jobId=${encodeURIComponent(newJobId)}`, {
        replace: true,
      });
    } catch (err) {
      console.error(err);
      setError("Failed to import workouts. Please try again.");
    } finally {
      setImporting(false);
    }
  };

  const updateWeeksWorkoutAtKey = (workoutKey, workoutPayload) => {
    const [, weekNum, workoutIdx] = workoutKey.split("-");
    const weekNumber = parseInt(weekNum, 10);
    const workoutIndex = parseInt(workoutIdx, 10);

    setWeeks((prev) =>
      prev.map((week) =>
        week.week === weekNumber
          ? {
              ...week,
              workouts: week.workouts.map((item, idx) =>
                idx === workoutIndex
                  ? {
                      ...item,
                      workout: {
                        ...workoutToTemplateInfo(workoutPayload),
                        exercises: workoutPayload.exercises || [],
                      },
                    }
                  : item
              ),
            }
          : week
      )
    );
  };

  const handleSaveSingle = async (payload) => {
    if (!selectedWorkoutKey) {
      alert("No workout selected.");
      return;
    }

    if (isProgramDestination) {
      setSaving(true);
      try {
        updateWeeksWorkoutAtKey(selectedWorkoutKey, payload);
        setStatusMessage("Workout updated in preview.");
        setTimeout(() => setStatusMessage(""), 3000);
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!selectedClientId) {
      alert("Select a client before saving.");
      return;
    }
    if (!payload?.date) {
      alert("Please pick a date before saving.");
      return;
    }

    setSaving(true);
    try {
      await addWorkoutForUser(selectedClientId, payload);
      const savedDate = payload.date;

      // Parse selected workout key
      const [, weekNum, workoutIdx] = selectedWorkoutKey.split("-");
      const weekNumber = parseInt(weekNum, 10);
      const workoutIndex = parseInt(workoutIdx, 10);

      // Track that this workout was saved
      setSavedDates((prev) => ({
        ...prev,
        [selectedWorkoutKey]: savedDate,
      }));

      const dateUnix = workoutDateToUnixSeconds(payload.date);

      setWeeks((prev) =>
        prev.map((week) =>
          week.week === weekNumber
            ? {
                ...week,
                workouts: week.workouts.map((item, idx) =>
                  idx === workoutIndex
                    ? {
                        ...item,
                        workout: {
                          ...payload,
                          date: dateUnix,
                        },
                      }
                    : item
                ),
              }
            : week
        )
      );
      setStatusMessage(
        `Workout saved to ${momentFromWorkoutTimestamp(savedDate).format(
          "MMM D, YYYY"
        )}`
      );
      setTimeout(() => setStatusMessage(""), 3000);
    } catch (err) {
      console.error(err);
      alert("Failed to save workout. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAllToProgram = async () => {
    if (!weeks.length) {
      alert("Import workouts before saving to a program.");
      return;
    }
    if (!coachId) {
      alert("Coach profile not loaded. Try refreshing the page.");
      return;
    }
    if (programTargetId === "new" && !newProgramName.trim()) {
      alert("Enter a name for the new program.");
      return;
    }
    if (programTargetId !== "new") {
      const program = programs.find((p) => p.id === programTargetId);
      if (!program) {
        alert("Selected program was not found. Choose another program.");
        return;
      }
    }

    const totalWorkouts = weeks.reduce(
      (sum, w) => sum + (w.workouts?.length || 0),
      0
    );
    if (
      !window.confirm(
        `Save ${totalWorkouts} workout${totalWorkouts !== 1 ? "s" : ""} as templates` +
          (programTargetId === "new"
            ? ` and create program "${newProgramName.trim()}"?`
            : programMergeMode === "append"
              ? ` and append ${weeks.length} week${weeks.length !== 1 ? "s" : ""} to the selected program?`
              : ` and replace the selected program's structure?`)
      )
    ) {
      return;
    }

    setSavingToProgram(true);
    setError("");
    try {
      const templateWeeks = await buildTemplateWeeksFromImport(
        weeks,
        createWorkoutTemplate,
        coachId
      );

      if (programTargetId === "new") {
        const created = await createProgram({
          coachId,
          name: newProgramName.trim(),
          lengthWeeks: templateWeeks.length,
          weeks: templateWeeks,
        });
        const programId = created?.program?.id;
        setSavedProgramId(programId || null);
        setStatusMessage(
          `Created program "${newProgramName.trim()}" with ${totalWorkouts} template${totalWorkouts !== 1 ? "s" : ""}.`
        );
      } else {
        const program = programs.find((p) => p.id === programTargetId);
        const existingWeeks = Array.isArray(program.weeks)
          ? program.weeks.map((w) => (Array.isArray(w) ? [...w] : []))
          : [];
        const mergedWeeks = mergeProgramWeeks(
          existingWeeks,
          templateWeeks,
          programMergeMode
        );
        await updateProgram(programTargetId, {
          name: program.name,
          lengthWeeks: mergedWeeks.length,
          weeks: mergedWeeks,
        });
        setSavedProgramId(programTargetId);
        setStatusMessage(
          programMergeMode === "append"
            ? `Appended ${weeks.length} week${weeks.length !== 1 ? "s" : ""} to "${program.name}".`
            : `Updated "${program.name}" with imported workouts.`
        );
      }
    } catch (err) {
      console.error(err);
      setError("Failed to save to program. Please try again.");
    } finally {
      setSavingToProgram(false);
    }
  };

  if (userInfoLoading) {
    return (
      <Container sx={{ mt: 8 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (!isCoach) {
    return (
      <Container sx={{ mt: 8 }}>
        <Alert severity="warning">
          This page is only available to coaches.
        </Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 6, mb: 8 }}>
      <Typography variant="h4" gutterBottom>
        Import Workouts
      </Typography>

      {importBlocked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {awaitingUrlJobHydrate
            ? "Loading import job…"
            : urlJob &&
                (urlJob.status === "pending" || urlJob.status === "running")
              ? "This import is still running. You can leave this page and reopen it from the header; the Import button stays disabled until it finishes."
              : activeJobs.length > 1
                ? `${activeJobs.length} workout imports are running. Open one from the header or wait before starting another.`
                : "A workout import is already running. Open it from the header or wait before starting another."}
        </Alert>
      )}

      <Typography sx={{ mb: 2 }}>
        Paste or upload workout text and we&apos;ll format it. Save to a
        client&apos;s calendar with scheduled dates, or build program templates
        for a new or existing program.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Save to
          </Typography>
          <RadioGroup
            row
            value={saveDestination}
            onChange={(e) => {
              setSaveDestination(e.target.value);
              setSavedProgramId(null);
            }}
          >
            <FormControlLabel
              value="client"
              control={<Radio />}
              label="Client calendar"
            />
            <FormControlLabel
              value="program"
              control={<Radio />}
              label="Program templates"
            />
          </RadioGroup>
        </FormControl>

        {isProgramDestination && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel id="program-target-label">Program</InputLabel>
                <Select
                  labelId="program-target-label"
                  label="Program"
                  value={programTargetId}
                  onChange={(e) => {
                    setProgramTargetId(e.target.value);
                    setSavedProgramId(null);
                  }}
                  disabled={loadingPrograms}
                >
                  <MenuItem value="new">Create new program</MenuItem>
                  {programs.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {programTargetId === "new" ? (
              <Grid item xs={12} md={6}>
                <TextField
                  label="New program name"
                  fullWidth
                  size="small"
                  value={newProgramName}
                  onChange={(e) => setNewProgramName(e.target.value)}
                />
              </Grid>
            ) : (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="program-merge-label">
                    Update existing program
                  </InputLabel>
                  <Select
                    labelId="program-merge-label"
                    label="Update existing program"
                    value={programMergeMode}
                    onChange={(e) => setProgramMergeMode(e.target.value)}
                  >
                    <MenuItem value="replace">
                      Replace program with import
                    </MenuItem>
                    <MenuItem value="append">
                      Append imported weeks
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        )}

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          {!isProgramDestination && (
          <Grid item xs={12} md={6}>
            {loadingClients ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  height: 56,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  bgcolor: "background.paper",
                }}
              >
                <CircularProgress size={24} />
                <Typography color="text.secondary">
                  Loading clients...
                </Typography>
              </Box>
            ) : (
              <FormControl fullWidth>
                <InputLabel id="client-select-label">Client</InputLabel>
                <Select
                  labelId="client-select-label"
                  label="Client"
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                >
                  {coachProfile && (
                    <MenuItem key={coachProfile.id} value={coachProfile.id}>
                      {coachProfile.name} (Me) — {coachProfile.email}
                    </MenuItem>
                  )}
                  {clients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.name} — {client.email}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          )}
          <Grid item xs={12} md={isProgramDestination ? 4 : 3}>
            <Select
              label="Default Weight Unit"
              fullWidth
              size="small"
              value={defaultWeightUnit}
              onChange={(e) => setDefaultWeightUnit(e.target.value)}
            >
              <MenuItem value="lbs">Pounds (lbs)</MenuItem>
              <MenuItem value="kg">Kilograms (kg)</MenuItem>
              <MenuItem value="bodyweight">Bodyweight</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={isProgramDestination ? 4 : 3}>
            <Button
              component="label"
              variant="outlined"
              fullWidth
              sx={{ mt: { xs: 2, md: 0 } }}
            >
              Upload .txt
              <input
                type="file"
                hidden
                accept=".txt,.md,text/plain"
                onChange={handleFileUpload}
              />
            </Button>
          </Grid>
          <Grid item xs={12} md={isProgramDestination ? 4 : 3}>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={importing || importBlocked}
              fullWidth
            >
              {importing
                ? "Starting…"
                : importBlocked
                  ? awaitingUrlJobHydrate
                    ? "Loading job…"
                    : "Import in progress…"
                  : "Import"}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Paste workout text"
              placeholder="Paste the workout text here..."
              multiline
              minRows={10}
              fullWidth
              value={rawText}
              onChange={(e) => setRawText(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {statusMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {statusMessage}
          {savedProgramId && (
            <>
              {" "}
              <Link component={RouterLink} to="/templates">
                Open templates
              </Link>
            </>
          )}
        </Alert>
      )}

      <Paper ref={resultsPaperRef} sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h6">Imported Workouts</Typography>
          {weeks.length > 0 && programLength > 1 && (
            <Chip
              label={`${programLength} Week Program`}
              color="primary"
              size="small"
            />
          )}
          {isProgramDestination && weeks.length > 0 && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSaveAllToProgram}
              disabled={savingToProgram}
              sx={{ ml: "auto" }}
            >
              {savingToProgram ? (
                <>
                  <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                  Saving…
                </>
              ) : programTargetId === "new" ? (
                "Create program from import"
              ) : (
                "Save to program"
              )}
            </Button>
          )}
        </Box>
        {isProgramDestination && weeks.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Review each workout below, then save all to create templates and
            update the program. Use Save on each workout to keep edits in the
            preview.
          </Typography>
        )}
        {!weeks.length ? (
          <Typography color="text.secondary">
            No workouts parsed yet. Paste or upload text, then click Import.
          </Typography>
        ) : (
          <Box>
            {showWorkoutTabs && (
              <Tabs
                value={selectedWorkoutKey || false}
                onChange={(_e, newKey) => setSelectedWorkoutKey(newKey)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  mb: 2,
                  borderBottom: 1,
                  borderColor: "divider",
                  minHeight: 48,
                }}
              >
                {workoutTabs.map(({ key, week, label, item }) => {
                  const tabLabel = showWeekPrefixOnTabs ? `W${week} · ${label}` : label;
                  const saved = !!savedDates[key];
                  const warningCount = item.warnings?.length || 0;
                  const labelNode =
                    warningCount > 0 ? (
                      <Badge
                        badgeContent={warningCount}
                        color="warning"
                        sx={{ "& .MuiBadge-badge": { fontSize: "0.65rem", height: 16, minWidth: 16 } }}
                      >
                        {tabLabel}
                      </Badge>
                    ) : (
                      tabLabel
                    );
                  return (
                    <Tab
                      key={key}
                      value={key}
                      label={
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <span>{labelNode}</span>
                          {saved && (
                            <Chip
                              label="Saved"
                              size="small"
                              color="success"
                              sx={{ height: 20, "& .MuiChip-label": { px: 0.75, fontSize: "0.65rem" } }}
                            />
                          )}
                        </Stack>
                      }
                      sx={{ textTransform: "none", minHeight: 48 }}
                    />
                  );
                })}
              </Tabs>
            )}

            {selectedWorkoutKey ? (
              (() => {
                const [, weekNum, workoutIdx] = selectedWorkoutKey.split("-");
                const weekNumber = parseInt(weekNum, 10);
                const workoutIndex = parseInt(workoutIdx, 10);
                const selectedWeek = weeks.find((w) => w.week === weekNumber);
                const selectedWorkout = selectedWeek?.workouts[workoutIndex];

                if (!selectedWorkout) {
                  return (
                    <Typography color="text.secondary">
                      Workout not found.
                    </Typography>
                  );
                }

                const workoutWithDate = {
                  ...(selectedWorkout.workout || {}),
                  date: momentFromWorkoutTimestamp(
                    selectedWorkout.workout?.date
                  ),
                };

                return (
                  <AddWorkoutDetails
                    key={selectedWorkoutKey}
                    open
                    mode={isProgramDestination ? "template" : "workout"}
                    userId={selectedClientId}
                    workout={
                      isProgramDestination
                        ? selectedWorkout.workout || {}
                        : workoutWithDate
                    }
                    onSave={handleSaveSingle}
                    handleClose={() => {}}
                    draftTemplateMode
                    submitButtonId="import-workout-save-button"
                    onDeleteDraft={() => {
                      setWeeks((prev) =>
                        prev
                          .map((week) =>
                            week.week === weekNumber
                              ? {
                                  ...week,
                                  workouts: week.workouts.filter(
                                    (_, i) => i !== workoutIndex
                                  ),
                                }
                              : week
                          )
                          .filter((week) => week.workouts.length > 0)
                      );
                      const updatedWeeks = weeks
                        .map((week) =>
                          week.week === weekNumber
                            ? {
                                ...week,
                                workouts: week.workouts.filter(
                                  (_, i) => i !== workoutIndex
                                ),
                              }
                            : week
                        )
                        .filter((week) => week.workouts.length > 0);

                      if (updatedWeeks.length > 0) {
                        const firstWeek = updatedWeeks[0];
                        if (firstWeek.workouts.length > 0) {
                          setSelectedWorkoutKey(`week-${firstWeek.week}-0`);
                        } else {
                          setSelectedWorkoutKey(null);
                        }
                      } else {
                        setSelectedWorkoutKey(null);
                      }
                    }}
                  />
                );
              })()
            ) : (
              <Typography color="text.secondary">
                Select a workout to edit and save.
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default WorkoutImport;
